import { useIsMobile } from "@shared/hooks/useIsMobile";
import AngleLeftIcon from "@svg/angle-left";
import { toFarsiNumber } from "@utils/digits";
import { FC } from "react";
import { twMerge } from "tailwind-merge";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  // eslint-disable-next-line no-unused-vars
  onPageChange: (page: number) => void;
  pageItemsToShow: number;
}

const itemsClassName =
  "px-2 min-w-10 h-10 flex items-center justify-center rounded-md bg-white text-fds-primary body-md ml-1";

const getPageNumbers = (
  currentPage: number,
  totalPages: number,
  pageItemsToShow: number
): number[] => {
  const pageNumbers: number[] = [];
  if (totalPages <= pageItemsToShow) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    const halfPageItemsToShow = Math.floor(pageItemsToShow / 2);
    let startPage = currentPage - halfPageItemsToShow;
    let endPage = currentPage + halfPageItemsToShow - 1;

    if (startPage < 1) {
      startPage = 1;
      endPage = pageItemsToShow;
    } else if (endPage > totalPages) {
      endPage = totalPages;
      startPage = totalPages - pageItemsToShow + 1;
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
  }

  return pageNumbers;
};

const Pagination: FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageItemsToShow,
}) => {
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const isMobile = useIsMobile(640);

  const pagesData = getPageNumbers(currentPage, totalPages, pageItemsToShow);

  return (
    <div className="flex items-center justify-center">
      <button
        className="h-10 px-2 flex items-center justify-center text-sm rounded-md bg-white text-[#3064C0] sm:ml-1 disabled:text-[#D4D4D8]"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <AngleLeftIcon
          width={isMobile ? 16 : 24}
          height={isMobile ? 16 : 24}
          className="-rotate-180 ml-1"
        />
        {isMobile ? "" : "قبلی"}
      </button>

      {currentPage > pageItemsToShow && (
        <button className={itemsClassName} onClick={() => handlePageChange(1)}>
          {toFarsiNumber(1)}
        </button>
      )}

      {currentPage > pageItemsToShow && (
        <span className={itemsClassName}>...</span>
      )}

      {pagesData.map((page) => (
        <button
          key={page}
          className={twMerge(
            itemsClassName,
            page === currentPage &&
              "bg-fds-brand-light border-transparent text-fds-brand cursor-default",
            page === totalPages && "ml-0"
          )}
          onClick={() => handlePageChange(page)}
        >
          {toFarsiNumber(page)}
        </button>
      ))}

      {currentPage < totalPages - pageItemsToShow + 1 && (
        <span className={itemsClassName}>...</span>
      )}

      {currentPage < totalPages &&
        totalPages > pageItemsToShow &&
        !pagesData.includes(totalPages) && (
          <button
            className={twMerge(itemsClassName, "ml-0")}
            onClick={() => handlePageChange(totalPages)}
          >
            {toFarsiNumber(totalPages)}
          </button>
        )}

      <button
        className="h-10 px-2 flex items-center justify-center text-sm rounded-md bg-white text-fds-brand sm:mr-1 disabled:text-fds-disable"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        {isMobile ? "" : "بعدی"}
        <AngleLeftIcon
          width={isMobile ? 16 : 24}
          height={isMobile ? 16 : 24}
          className="mr-1"
        />
      </button>
    </div>
  );
};

export default Pagination;
